import { Group, Stack } from '@mantine/core'
import { useEffect, useState } from 'react'
import { PaymentParams } from 'app/models/payments-params'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import PaymentModal from './payment-modal'
import { ContractType } from 'app/models/common-types'
import { useDispatch, useSelector } from 'react-redux'
import { getSenders } from 'app/state/ducks/payments/selectors'
import { changeFrontingOnPayment, loadReceivers, loadSenders } from 'app/state/ducks/payments/thunks'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { StatusPill } from 'app/views/components/pill/status-pill'
import { loadNotesByContract } from 'app/state/ducks/notes/thunks'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import PaymentDetailsInfo from './payment-details-info'
import PaymentWireInstructionsInfo from './payment-wire-instruction-info'
import { PaymentLenderAllocationTable } from './payment-lender-allocation-table'
import MainDrawer from 'app/views/components/Drawer/MainDrawer'
import SecondaryButton from 'app/views/components/buttons/SecondaryButton'

interface Props {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  row?: PaymentParams
}

export default function ReleaseQueueDrawer({ open, setOpen, row }: Props) {
  const dispatch = useDispatch()
  const senders = useSelector(getSenders)
  const [loading, setLoading] = useState<boolean>(false)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [isCanceled, setIsCanceled] = useState<boolean>(false)

  useEffect(() => {
    // The drawer stays mounted while closed, and reopening the same row keeps
    // the same `row` reference, so the loads key on `open` as well.
    if (!row || !open) {
      return
    }
    dispatch(loadNotesByContract(row.id, ContractType.Payments))
    dispatch(loadSenders(row.id))
    dispatch(loadReceivers(row.id))
    // The receivers table checks each lender's KYC and the senders table
    // names them from the entity list. The grid no longer loads it (its alert
    // comes with its one read), so the drawer loads it on open (ADR_LOS-030
    // rule 5) — EVERY open, not only when the store is empty: another page may
    // have filled it earlier in the session, and a KYC change or a new lender
    // since then would otherwise show stale here while the grid's alert is
    // already current.
    dispatch(loadEntities())
    if (row.id == "1") changeFronting(row.payment.fronting)
  }, [row, open])

  const isDrawer = true

  const changeFronting = async (value: boolean) => {
    setLoading(true)
    const response: any = await dispatch(changeFrontingOnPayment(row, value))
    setLoading(false)
    if (response.success === false) {
      ErrorNotification({
        title: 'Change Fronting status Failed',
        message: response.payload ?? 'Failed to change fronting',
      }, isDrawer)
      return
    }

    SuccessNotification({
      title: 'Successfully changed fronting status 2',
      message: 'Status is updated.',
    }, isDrawer)
  }

  if (!row) {
    return <></>
  }
  return (
    <>
      <MainDrawer
        opened={open}
        setOpen={setOpen}
        titleText={row.payment.facilityName ?? ''}
        statusPill={<StatusPill status={row.status} payment={row} />}
        footer={
          ['Queued', 'Released', 'Rejected'].includes(row.status) ? (
            <Group position="right">
              <SecondaryButton
                variant="outline"
                onClick={() => {
                  setIsCanceled(true)
                  setOpen(false)
                  setOpenModal(true)
                }}
              >
                Cancel
              </SecondaryButton>

              <PrimaryButton
                disabled={
                  loading ||
                  (
                    row.status === 'Queued' &&
                    !row.submitted &&
                    !row.payment.fronting &&
                    senders.some((sender) => !sender.paymentReceived)
                  )}
                onClick={() => {
                  setIsCanceled(false)
                  setOpen(false)
                  setOpenModal(true)
                }}
              >
                {row.status === 'Rejected'
                  ? "Queue"
                  : row.status === 'Released'
                    ? "Settle"
                    : row.submitted ? "Release" : "Submit"}
              </PrimaryButton>
            </Group>) : null
        }
      >
        <Stack style={{ gap: "10px" }} >
          <PaymentDetailsInfo payment={row} isDrawer={isDrawer} />
          <PaymentLenderAllocationTable payment={row} closeDrawer={() => setOpen(false)} isDrawer={isDrawer} />
          {row.payment.transactionType === 'NewBorrowingTransaction' && <PaymentWireInstructionsInfo />}

        </Stack>
      </MainDrawer>

      <PaymentModal
        open={openModal}
        setOpen={setOpenModal}
        payment={row}
        isCanceled={isCanceled}
      />
    </>
  )
}
